import { Box, CircularProgress } from "@mui/material";
import { CoverageTierEnum, RateDesignEnum } from "@platform/openapi-types";
import { type JSX, useCallback, useMemo } from "react";

import { useGetRateScheduleStats } from "../../../hooks/useGetRateScheduleStats";
import { useIsVolumeBased } from "../../../hooks/useIsVolumeBased";
import { useUpdateRate, useUpdateProposalRate } from "../../../hooks/useRates";
import { isVolumeBasedRate } from "../../../utils/premium-calculations";
import { isCriticalIllnessCoverageType } from "../../../utils/rate-helpers";
import {
  formatAgeBandLabel,
  formatRateValue,
  getEnrolledLabel,
  getTierEnrolledLabel,
  groupRatesByAgeBandTobacco,
  type AgeBandTobaccoPair,
} from "../../../utils/rateFormatters";
import { parseIntBaseTen } from "../../../utils/transforms";
import {
  buildRateFieldPath,
  quantityFieldOf,
  quantityFieldOfRate,
  quantityValueOf,
} from "../../../utils/rateFieldPath";

import { CompanionRatesSection } from "./CompanionRatesSection";
import { DebouncedNumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import {
  RenewalGroupHeaderCell,
  RenewalRowCells,
  RenewalSubHeaderCells,
} from "./RenewalRateCell";
import {
  resolveDestinationPlans,
  useAgeBandedRateCopy,
} from "./useAgeBandedRateCopy";
import {
  AG_BORDER,
  AG_HEADER_BG_COLOR,
  FONT_SIZE,
  SPACE_BETWEEN,
  gridCellSx,
  gridRowSx,
  headerCellSx,
  inputSx,
} from "./constants";

import type { RateCopyColumn } from "./useAgeBandedRateCopy";
import type { ColDef } from "ag-grid-community";
import type { AxiosInstance } from "axios";
import type { FC } from "react";
import type { Rate, RateSchedule } from "../../../types";
import type { RateCellField } from "../../../utils/rateFieldPath";
import type { BenefitPlan, BenefitPlanGroup } from "../../../types/models";

export interface AgeBandedTobaccoRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  coverageType?: string;
  onRateSaved: () => void;
  readOnly?: boolean;
  benefitPlanGroup?: BenefitPlanGroup;
  /** All plans in the coverage offered as copy destinations. */
  copyDestinationPlans?: BenefitPlan[];
}

export const buildAgeBandedTobaccoColumns = (
  _group: BenefitPlanGroup,
): ColDef[] => [];

export const buildAgeBandedTobaccoSummaryColumns = (
  _planId: string,
  _planName: string,
): ColDef[] => [];

// Rate-cell labels, shared by the copy-column config, the copy-button aria labels, and the
// renewal cells so the three stay in sync (and to satisfy no-duplicate-string).
const NON_TOBACCO_EE = "Non-Tobacco EE";
const NON_TOBACCO_SP = "Non-Tobacco SP";
const TOBACCO_EE = "Tobacco EE";
const TOBACCO_SP = "Tobacco SP";
const TOBACCO = "Tobacco";
const NON_TOBACCO = "Non-Tobacco";

// These tables are dense; give each track a min wide enough to hold a rate value / header
// word on one line (cells inherit break-word and split mid-word/number when narrower), and
// scroll horizontally within the pinned benefit-plan cell rather than squishing. Applies to
// every column, quoted plans included. When renewal is shown, `renewalTracks` read-only
// columns are appended and the wider table scrolls within the pinned cell.
//   standard: Age Band | Tobacco Qty | Tobacco Rate | NonTobacco Qty | NonTobacco Rate (+2 renewal)
//   EE/SP:    Age Band | NT EE Qty/Rate | NT SP Qty/Rate | T EE Qty/Rate | T SP Qty/Rate (+4 renewal)
const gridContainerSx = (
  dataTracks: number,
  showRenewal: boolean,
  renewalTracks: number,
): Record<string, unknown> => ({
  display: "grid",
  gridTemplateColumns: `minmax(90px, 1fr) repeat(${
    showRenewal ? dataTracks + renewalTracks : dataTracks
  }, minmax(76px, 1fr))`,
  width: "100%",
  overflowX: "auto",
});

export const AgeBandedTobaccoRenderer: FC<AgeBandedTobaccoRendererProps> = ({
  plan,
  axiosInstance,
  coverageType,
  onRateSaved,
  readOnly,
  benefitPlanGroup,
  copyDestinationPlans,
}) => {
  const {
    rateSchedule,
    companionRateSchedule,
    spouseAgeBandedSchedule,
    rates,
    companionRates,
    renewal,
    isLoading,
  } = useGetRateScheduleStats(plan);
  const showRenewal = renewal.showRenewal;

  // Read off the primary schedule alone: the EE/SP layout renders only
  // employee and spouseOnly rates, so letting a spouse schedule select it on
  // its own would hide a primary schedule of untagged rows.
  const hasEmpSpouseSplit = useMemo(
    () =>
      rates.some(
        (rate) =>
          rate.coverageTierCode === CoverageTierEnum.employee ||
          rate.coverageTierCode === CoverageTierEnum.spouseOnly,
      ),
    [rates],
  );

  // Carriers that split a tobacco quote send the spouse table as its own
  // schedule, so the EE/SP layout has to read both to fill its SP columns.
  // Gated on that layout because it is the only one that renders spouse rates
  // at all; the standard layout would carry them as rows of empty cells.
  // A band the spouse table quotes and the employee table does not still gets
  // a row, with its EE cells empty. Dropping it would hide a rate the carrier
  // sent, which is the failure this whole path exists to avoid.
  const displayedRates = useMemo(
    () =>
      hasEmpSpouseSplit
        ? [...rates, ...(spouseAgeBandedSchedule?.rates ?? [])]
        : rates,
    [hasEmpSpouseSplit, rates, spouseAgeBandedSchedule],
  );

  const ageBandGroups: AgeBandTobaccoPair[] = useMemo(
    () => groupRatesByAgeBandTobacco(displayedRates),
    [displayedRates],
  );

  // Mutation hook for updating rates
  const updateRateMutation = useUpdateRate(axiosInstance!);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance!);
  const rateMutation =
    plan.rateScheduleSource === "proposal"
      ? updateProposalRateMutation
      : updateRateMutation;

  // Determine if rates use volume-based units (volumePer*, per*OfSalary)
  const isVolumeBased = useIsVolumeBased(rates);
  const quantityFieldName = quantityFieldOf(isVolumeBased);

  // A displayed rate can belong to either schedule, so edits and cell keys
  // route by the rate's own id rather than assuming the primary schedule.
  // Routing by tier instead would misdirect a spouseOnly rate that lives in
  // the primary schedule.
  const scheduleByRateId = useMemo(
    () =>
      new Map(
        [rateSchedule, spouseAgeBandedSchedule].flatMap((schedule) =>
          schedule?.id == null
            ? []
            : (schedule.rates ?? [])
                .filter((rate) => rate.id != null)
                .map((rate): [string, RateSchedule] => [rate.id!, schedule]),
        ),
      ),
    [rateSchedule, spouseAgeBandedSchedule],
  );

  const resolveSchedule = useCallback(
    (rate: Rate): RateSchedule | undefined =>
      (rate.id ? scheduleByRateId.get(rate.id) : undefined) ?? rateSchedule,
    [scheduleByRateId, rateSchedule],
  );

  const fieldPathOf = useCallback(
    (rate: Rate | undefined, field: RateCellField): string | undefined => {
      const schedule = rate && resolveSchedule(rate);
      return rate && schedule
        ? buildRateFieldPath(schedule, rate, field)
        : undefined;
    },
    [resolveSchedule],
  );

  const isCriticalIllness = isCriticalIllnessCoverageType(coverageType);

  // Quantity column label adapts to unit type
  const enrolledLabel = getEnrolledLabel(
    isVolumeBased,
    rates[0]?.unit,
    coverageType,
  );

  const handleCommit =
    (parser: (val: string) => number, rateField: keyof Rate) =>
    (rate: Rate, value: string): void => {
      const scheduleId = resolveSchedule(rate)?.id;
      if (!axiosInstance || !scheduleId || !rate.id) return;
      const numericValue = parser(value);
      if (isNaN(numericValue) || numericValue === rate[rateField]) return;

      rateMutation.mutate(
        {
          scheduleId,
          rateId: rate.id,
          benefitPlanId: plan.id,
          rate: { [rateField]: numericValue },
        },
        { onSuccess: () => onRateSaved() },
      );
    };

  const handleLivesCommit = handleCommit(parseIntBaseTen, "numberOfLives");
  const handleVolumeCommit = handleCommit(parseFloat, "volume");
  const handleQuantityCommit = (rate: Rate, value: string): void =>
    isVolumeBasedRate(rate)
      ? handleVolumeCommit(rate, value)
      : handleLivesCommit(rate, value);

  const handleRateCommit = handleCommit(parseFloat, "rate");

  // Copy columns depend on the layout: standard splits by tobacco status, the
  // EE/SP layout splits by tobacco status and coverage tier.
  const copyColumns: RateCopyColumn[] = useMemo(
    () =>
      hasEmpSpouseSplit
        ? [
            {
              id: "nt-ee",
              label: NON_TOBACCO_EE,
              matches: (rate: Rate) =>
                !rate.isTobaccoRated &&
                rate.coverageTierCode === CoverageTierEnum.employee,
            },
            {
              id: "nt-sp",
              label: NON_TOBACCO_SP,
              matches: (rate: Rate) =>
                !rate.isTobaccoRated &&
                rate.coverageTierCode === CoverageTierEnum.spouseOnly,
            },
            {
              id: "t-ee",
              label: TOBACCO_EE,
              matches: (rate: Rate) =>
                rate.isTobaccoRated === true &&
                rate.coverageTierCode === CoverageTierEnum.employee,
            },
            {
              id: "t-sp",
              label: TOBACCO_SP,
              matches: (rate: Rate) =>
                rate.isTobaccoRated === true &&
                rate.coverageTierCode === CoverageTierEnum.spouseOnly,
            },
          ]
        : [
            {
              id: "tobacco",
              label: TOBACCO,
              matches: (rate: Rate) => rate.isTobaccoRated === true,
            },
            {
              id: "non-tobacco",
              label: NON_TOBACCO,
              matches: (rate: Rate) => !rate.isTobaccoRated,
            },
          ],
    [hasEmpSpouseSplit],
  );

  const destinationPlans = useMemo(
    () => resolveDestinationPlans(plan, copyDestinationPlans, benefitPlanGroup),
    [plan, copyDestinationPlans, benefitPlanGroup],
  );

  const { renderCopyButton, copyDialog } = useAgeBandedRateCopy({
    plan,
    rates,
    rateSchedule,
    columns: copyColumns,
    rateDesign: RateDesignEnum.ageBandedTobacco,
    destinationPlans,
    updateRateMutation,
    updateProposalRateMutation,
    onRateSaved,
  });

  // The last current-rate column gets a right divider only when renewal columns follow it.
  const currentEndBorderRight = showRenewal ? AG_BORDER : "none";

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!rateSchedule) {
    return (
      <Box sx={{ p: 1, fontSize: "13px", color: "text.secondary" }}>
        No rate schedule
      </Box>
    );
  }

  const headerBox = (
    <Box
      sx={{
        display: "flex",
        justifyContent: SPACE_BETWEEN,
        alignItems: "center",
        height: "36px",
        backgroundColor: AG_HEADER_BG_COLOR,
        borderBottom: AG_BORDER,
        px: 1,
      }}
    >
      <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>
        Age Banded Tobacco ({ageBandGroups.length} bands)
      </Box>
    </Box>
  );

  // EE/SP split layout (9 columns)
  if (hasEmpSpouseSplit) {
    const employeeEnrolledLabel = getTierEnrolledLabel(
      displayedRates,
      CoverageTierEnum.employee,
      coverageType,
    );
    const spouseEnrolledLabel = getTierEnrolledLabel(
      displayedRates,
      CoverageTierEnum.spouseOnly,
      coverageType,
    );
    return (
      <Box
        sx={{ width: "100%", height: "100%", position: "relative", zIndex: 1 }}
      >
        {headerBox}
        <Box sx={gridContainerSx(8, showRenewal, 4)}>
          {/* Group header row */}
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }}>Age Band</Box>
            <Box
              sx={{
                ...headerCellSx,
                gridColumn: "span 4",
                justifyContent: "center",
                fontWeight: 600,
              }}
            >
              {NON_TOBACCO}
            </Box>
            <Box
              sx={{
                ...headerCellSx,
                gridColumn: "span 4",
                justifyContent: "center",
                fontWeight: 600,
                borderRight: currentEndBorderRight,
              }}
            >
              {TOBACCO}
            </Box>
            <RenewalGroupHeaderCell
              show={showRenewal}
              label={renewal.columnLabel}
              span={4}
            />
          </Box>
          {/* Column labels row */}
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }} />
            <Box sx={{ ...headerCellSx }}>EE {employeeEnrolledLabel}</Box>
            <Box
              sx={{
                ...headerCellSx,
                justifyContent: SPACE_BETWEEN,
                gap: 0.5,
              }}
            >
              <span>EE Rate</span>
              {!readOnly && renderCopyButton("nt-ee", NON_TOBACCO_EE)}
            </Box>
            <Box sx={{ ...headerCellSx }}>SP {spouseEnrolledLabel}</Box>
            <Box
              sx={{
                ...headerCellSx,
                justifyContent: SPACE_BETWEEN,
                gap: 0.5,
              }}
            >
              <span>SP Rate</span>
              {!readOnly && renderCopyButton("nt-sp", NON_TOBACCO_SP)}
            </Box>
            <Box sx={{ ...headerCellSx }}>EE {employeeEnrolledLabel}</Box>
            <Box
              sx={{
                ...headerCellSx,
                justifyContent: SPACE_BETWEEN,
                gap: 0.5,
              }}
            >
              <span>EE Rate</span>
              {!readOnly && renderCopyButton("t-ee", TOBACCO_EE)}
            </Box>
            <Box sx={{ ...headerCellSx }}>SP {spouseEnrolledLabel}</Box>
            <Box
              sx={{
                ...headerCellSx,
                borderRight: currentEndBorderRight,
                justifyContent: SPACE_BETWEEN,
                gap: 0.5,
              }}
            >
              <span>SP Rate</span>
              {!readOnly && renderCopyButton("t-sp", TOBACCO_SP)}
            </Box>
            <RenewalSubHeaderCells
              show={showRenewal}
              labels={["NT EE", "NT SP", "T EE", "T SP"]}
            />
          </Box>

          {ageBandGroups.map((group, rowIndex) => {
            const ageBandLabel = formatAgeBandLabel(group.lower, group.upper, {
              isHighestBand: rowIndex === ageBandGroups.length - 1,
              isCriticalIllness,
            });
            const isLastRow =
              rowIndex === ageBandGroups.length - 1 &&
              companionRates.length === 0;
            const baseCellIndex = rowIndex * 8;
            const cellSx = (extraSx?: object): Record<string, unknown> => ({
              ...gridCellSx,
              borderBottom: isLastRow ? "none" : AG_BORDER,
              ...extraSx,
            });

            const renderQty = (
              rate: Rate | undefined,
              label: string,
              cellIdx: number,
            ): JSX.Element => (
              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(rate, quantityFieldOfRate(rate))}
                sx={cellSx()}
              >
                {rate && (
                  <DebouncedNumericInput
                    serverValue={quantityValueOf(rate)}
                    onCommit={(value) => handleQuantityCommit(rate, value)}
                    allowDecimals={isVolumeBasedRate(rate)}
                    isCurrency={isVolumeBasedRate(rate)}
                    sx={inputSx}
                    gridId={`age-banded-tobacco-${plan.id}`}
                    cellIndex={baseCellIndex + cellIdx}
                    columnsInRow={8}
                    readOnly={readOnly}
                    inputProps={{ "aria-label": `${ageBandLabel} ${label}` }}
                  />
                )}
              </RateReviewCell>
            );

            const renderRate = (
              rate: Rate | undefined,
              label: string,
              cellIdx: number,
              last = false,
            ): JSX.Element => (
              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(rate, "rate")}
                sx={cellSx(last ? { borderRight: "none" } : undefined)}
              >
                {rate && (
                  <DebouncedNumericInput
                    serverValue={formatRateValue(rate.rate, rate.unit)}
                    onCommit={(value) => handleRateCommit(rate, value)}
                    allowDecimals
                    isCurrency
                    sx={inputSx}
                    gridId={`age-banded-tobacco-${plan.id}`}
                    cellIndex={baseCellIndex + cellIdx}
                    columnsInRow={8}
                    readOnly={readOnly}
                    inputProps={{ "aria-label": `${ageBandLabel} ${label}` }}
                  />
                )}
              </RateReviewCell>
            );

            return (
              <Box
                key={`age-band-${group.lower}-${group.upper}`}
                sx={gridRowSx}
              >
                <Box
                  sx={{
                    ...headerCellSx,
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {ageBandLabel}
                </Box>
                {renderQty(
                  group.nonTobaccoEeRate,
                  `Non-Tobacco EE ${employeeEnrolledLabel}`,
                  0,
                )}
                {renderRate(group.nonTobaccoEeRate, "Non-Tobacco EE Rate", 1)}
                {renderQty(
                  group.nonTobaccoSpRate,
                  `Non-Tobacco SP ${spouseEnrolledLabel}`,
                  2,
                )}
                {renderRate(group.nonTobaccoSpRate, "Non-Tobacco SP Rate", 3)}
                {renderQty(
                  group.tobaccoEeRate,
                  `Tobacco EE ${employeeEnrolledLabel}`,
                  4,
                )}
                {renderRate(group.tobaccoEeRate, "Tobacco EE Rate", 5)}
                {renderQty(
                  group.tobaccoSpRate,
                  `Tobacco SP ${spouseEnrolledLabel}`,
                  6,
                )}
                {renderRate(
                  group.tobaccoSpRate,
                  "Tobacco SP Rate",
                  7,
                  !showRenewal,
                )}
                <RenewalRowCells
                  show={showRenewal}
                  renewal={renewal}
                  rowLabelPrefix={ageBandLabel}
                  isLastRow={isLastRow}
                  cells={[
                    { rate: group.nonTobaccoEeRate, label: NON_TOBACCO_EE },
                    { rate: group.nonTobaccoSpRate, label: NON_TOBACCO_SP },
                    { rate: group.tobaccoEeRate, label: TOBACCO_EE },
                    { rate: group.tobaccoSpRate, label: TOBACCO_SP },
                  ]}
                />
              </Box>
            );
          })}

          {companionRates.length > 0 && companionRateSchedule && (
            <CompanionRatesSection
              companionRates={companionRates}
              companionRateSchedule={companionRateSchedule}
              planId={plan.id}
              totalColumns={showRenewal ? 13 : 9}
              renewalColumns={Number(showRenewal) * 4}
              onRateSaved={onRateSaved}
            />
          )}
        </Box>
        {copyDialog}
      </Box>
    );
  }

  // Standard layout (5 columns)
  return (
    <Box
      sx={{ width: "100%", height: "100%", position: "relative", zIndex: 1 }}
    >
      {headerBox}
      <Box sx={gridContainerSx(4, showRenewal, 2)}>
        {/* Sub-header Row - Group Labels */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>Age Band</Box>
          <Box
            sx={{
              ...headerCellSx,
              gridColumn: "span 2",
              justifyContent: "center",
              fontWeight: 600,
            }}
          >
            {TOBACCO}
          </Box>
          <Box
            sx={{
              ...headerCellSx,
              gridColumn: "span 2",
              justifyContent: "center",
              fontWeight: 600,
              borderRight: currentEndBorderRight,
            }}
          >
            {NON_TOBACCO}
          </Box>
          <RenewalGroupHeaderCell
            show={showRenewal}
            label={renewal.columnLabel}
            span={2}
          />
        </Box>
        {/* Column labels row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }} />
          <Box sx={{ ...headerCellSx }}>{enrolledLabel}</Box>
          <Box
            sx={{
              ...headerCellSx,
              justifyContent: SPACE_BETWEEN,
              gap: 0.5,
            }}
          >
            <span>Rate</span>
            {!readOnly && renderCopyButton("tobacco", TOBACCO)}
          </Box>
          <Box sx={{ ...headerCellSx }}>{enrolledLabel}</Box>
          <Box
            sx={{
              ...headerCellSx,
              borderRight: currentEndBorderRight,
              justifyContent: SPACE_BETWEEN,
              gap: 0.5,
            }}
          >
            <span>Rate</span>
            {!readOnly && renderCopyButton("non-tobacco", NON_TOBACCO)}
          </Box>
          <RenewalSubHeaderCells
            show={showRenewal}
            labels={[TOBACCO, NON_TOBACCO]}
          />
        </Box>

        {ageBandGroups.map((group, rowIndex) => {
          const ageBandLabel = formatAgeBandLabel(group.lower, group.upper, {
            isHighestBand: rowIndex === ageBandGroups.length - 1,
            isCriticalIllness,
          });
          const isLastRow =
            rowIndex === ageBandGroups.length - 1 &&
            companionRates.length === 0;
          const baseCellIndex = rowIndex * 4;

          return (
            <Box key={`age-band-${group.lower}-${group.upper}`} sx={gridRowSx}>
              <Box
                sx={{
                  ...headerCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {ageBandLabel}
              </Box>

              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(group.tobaccoRate, quantityFieldName)}
                sx={{
                  ...gridCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {group.tobaccoRate && (
                  <DebouncedNumericInput
                    serverValue={quantityValueOf(group.tobaccoRate)}
                    onCommit={(value) =>
                      handleQuantityCommit(group.tobaccoRate!, value)
                    }
                    allowDecimals={isVolumeBased}
                    isCurrency={isVolumeBased}
                    sx={inputSx}
                    gridId={`age-banded-tobacco-${plan.id}`}
                    cellIndex={baseCellIndex}
                    columnsInRow={4}
                    readOnly={readOnly}
                    inputProps={{
                      "aria-label": `${ageBandLabel} Tobacco ${enrolledLabel}`,
                    }}
                  />
                )}
              </RateReviewCell>

              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(group.tobaccoRate, "rate")}
                sx={{
                  ...gridCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {group.tobaccoRate && (
                  <DebouncedNumericInput
                    serverValue={formatRateValue(
                      group.tobaccoRate.rate,
                      group.tobaccoRate.unit,
                    )}
                    onCommit={(value) =>
                      handleRateCommit(group.tobaccoRate!, value)
                    }
                    allowDecimals
                    isCurrency
                    sx={inputSx}
                    gridId={`age-banded-tobacco-${plan.id}`}
                    cellIndex={baseCellIndex + 1}
                    columnsInRow={4}
                    readOnly={readOnly}
                    inputProps={{
                      "aria-label": `${ageBandLabel} Tobacco Rate`,
                    }}
                  />
                )}
              </RateReviewCell>

              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(group.nonTobaccoRate, quantityFieldName)}
                sx={{
                  ...gridCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {group.nonTobaccoRate && (
                  <DebouncedNumericInput
                    serverValue={quantityValueOf(group.nonTobaccoRate)}
                    onCommit={(value) =>
                      handleQuantityCommit(group.nonTobaccoRate!, value)
                    }
                    allowDecimals={isVolumeBased}
                    isCurrency={isVolumeBased}
                    sx={inputSx}
                    gridId={`age-banded-tobacco-${plan.id}`}
                    cellIndex={baseCellIndex + 2}
                    columnsInRow={4}
                    readOnly={readOnly}
                    inputProps={{
                      "aria-label": `${ageBandLabel} Non-Tobacco ${enrolledLabel}`,
                    }}
                  />
                )}
              </RateReviewCell>

              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(group.nonTobaccoRate, "rate")}
                sx={{
                  ...gridCellSx,
                  borderRight: currentEndBorderRight,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {group.nonTobaccoRate && (
                  <DebouncedNumericInput
                    serverValue={formatRateValue(
                      group.nonTobaccoRate.rate,
                      group.nonTobaccoRate.unit,
                    )}
                    onCommit={(value) =>
                      handleRateCommit(group.nonTobaccoRate!, value)
                    }
                    allowDecimals
                    isCurrency
                    sx={inputSx}
                    gridId={`age-banded-tobacco-${plan.id}`}
                    cellIndex={baseCellIndex + 3}
                    columnsInRow={4}
                    readOnly={readOnly}
                    inputProps={{
                      "aria-label": `${ageBandLabel} Non-Tobacco Rate`,
                    }}
                  />
                )}
              </RateReviewCell>
              <RenewalRowCells
                show={showRenewal}
                renewal={renewal}
                rowLabelPrefix={ageBandLabel}
                isLastRow={isLastRow}
                cells={[
                  { rate: group.tobaccoRate, label: TOBACCO },
                  { rate: group.nonTobaccoRate, label: NON_TOBACCO },
                ]}
              />
            </Box>
          );
        })}

        {companionRates.length > 0 && companionRateSchedule && (
          <CompanionRatesSection
            companionRates={companionRates}
            companionRateSchedule={companionRateSchedule}
            planId={plan.id}
            isProposalRateSchedule={plan.rateScheduleSource === "proposal"}
            totalColumns={showRenewal ? 7 : 5}
            renewalColumns={Number(showRenewal) * 2}
            onRateSaved={onRateSaved}
          />
        )}
      </Box>
      {copyDialog}
    </Box>
  );
};
